import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import { Link } from "react-router-dom";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/swiper-bundle.css";
import { getEvents } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";

// Функция форматирования в дд.мм.гггг
const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Функция форматирования времени
const formatTime = (isoDate) => {
  const date = new Date(isoDate);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Получение названия месяца
const getMonthName = (isoDate, language = "en") => {
  const date = new Date(isoDate);
  const month = date.getMonth();

  const monthNames = {
    en: [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ],
    ru: [
      "ЯНВ",
      "ФЕВ",
      "МАР",
      "АПР",
      "МАЙ",
      "ИЮН",
      "ИЮЛ",
      "АВГ",
      "СЕН",
      "ОКТ",
      "НОЯ",
      "ДЕК",
    ],
    kz: [
      "ҚАҢ",
      "АҚП",
      "НАУ",
      "СӘУ",
      "МАМ",
      "МАУ",
      "ШІЛ",
      "ТАМ",
      "ҚЫР",
      "ҚАЗ",
      "ҚАР",
      "ЖЕЛ",
    ],
  };

  return monthNames[language]
    ? monthNames[language][month]
    : monthNames.en[month];
};

// Получение дня месяца
const getDayOfMonth = (isoDate) => {
  const date = new Date(isoDate);
  return String(date.getDate()).padStart(2, "0");
};

// Проверка, актуально ли событие (с учётом периода)
// Показывает события, которые ещё не закончились
const isEventActive = (event) => {
  const targetDate = event.endDate || event.startDate;
  if (!targetDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(targetDate);
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return targetDay >= today;
};

function EventSlider() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const { currentLanguage } = useLanguage();

  const translations = {
    kz: {
      title: "Оқиғалар анонсы",
      moreDetails: "Толығырақ",
      noEvents: "Қазіргі уақытта алдағы оқиғалар жоқ",
      allEvents: "Барлық іс-шаралар",
    },
    ru: {
      title: "Анонс событий",
      moreDetails: "Подробнее",
      noEvents: "В настоящее время нет предстоящих событий",
      allEvents: "Все мероприятия",
    },
    en: {
      title: "Events Announcement",
      moreDetails: "More Details",
      noEvents: "There are currently no upcoming events",
      allEvents: "All Events",
    },
  };

  const t = translations[currentLanguage] || translations.kz;

  // Функция получения перевода с fallback
  const getTranslation = (translationsArray) => {
    if (!translationsArray || translationsArray.length === 0) return "";

    // Сначала ищем текущий язык
    const current = translationsArray.find(t => t.languageCode === currentLanguage);
    if (current?.textValue) return current.textValue;

    // Потом казахский
    const kz = translationsArray.find(t => t.languageCode === 'kz');
    if (kz?.textValue) return kz.textValue;

    // Потом русский
    const ru = translationsArray.find(t => t.languageCode === 'ru');
    if (ru?.textValue) return ru.textValue;

    // Потом английский
    const en = translationsArray.find(t => t.languageCode === 'en');
    if (en?.textValue) return en.textValue;

    return "";
  };

  useEffect(() => {
    const loadEvents = async () => {
      const response = await getEvents();

      if (response.events) {
        setEvents(response.events);
        const activeEvents = response.events.filter(event => isEventActive(event));
        const filtered =
          activeEvents.length >= 3
            ? activeEvents
            : [
              ...activeEvents,
              ...response.events.filter(
                event => !activeEvents.some(active => active.id === event.id)
              ),
            ].slice(0, 3);

        setFilteredEvents(filtered);
      }
    };

    loadEvents();
  }, []);

  return (
    <section className="event-slider-section">
      <div className="event-slider-section-container">
        <div className="event-title-line">
          <span className="line-events" />
          <h2 className="slider-title">{t.title}</h2>
          <span className="line-events" />
        </div>
        <div className="event-carousel">
          {filteredEvents.length > 0 ? (
            <Swiper
              className="swiper"
              modules={[Autoplay, EffectFade]}
              spaceBetween={0}
              slidesPerView={2}
              loop={filteredEvents.length > 2}
              grabCursor={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              breakpoints={{
                // Мобильные устройства
                0: {
                  slidesPerView: 1,
                },
                480: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 1,
                },
                // Планшеты
                769: {
                  slidesPerView: 2,
                },
                // Ноутбуки
                1024: {
                  slidesPerView: 2,
                },
                // Большие экраны
                1440: {
                  slidesPerView: 2,
                },
                // Full HD и больше
                1920: {
                  slidesPerView: 2,
                },
              }}
            >
              {filteredEvents.map((event, index) => {
                const eventName = getTranslation(event.eventName?.translations);
                const description = getTranslation(event.eventDescription?.translations);
                const place = getTranslation(event.eventPlace?.translations);

                return (
                  <SwiperSlide className="swiper-slide-events" key={index}>
                    <div className="event-card">
                      <div className="event-content">
                        {/* Круг с датой */}
                        <div className="event-date-circle">
                          <span className="event-date-month">
                            {getMonthName(event.startDate || event.endDate, currentLanguage)}
                          </span>
                          <span className="event-date-day">
                            {getDayOfMonth(event.startDate || event.endDate)}
                          </span>
                        </div>
                        <div className="event-info-container">
                          <div className="event-name-wrapper">
                            <h3 className="event-name">
                              {eventName.length > 50
                                ? eventName.slice(0, 50) + "..."
                                : eventName}
                            </h3>
                          </div>
                          <div className="event-meta">
                            {place && (
                              <div className="event-meta-item">
                                <i className="event-icon location-icon"></i>
                                <span className="event-place">{place}</span>
                              </div>
                            )}
                          </div>
                          <div className="event-description">
                            {description.length > 120
                              ? description.slice(0, 120) + "..."
                              : description}
                          </div>
                          <Link
                            to={`/events/${event.id}`}
                            className="event-more-btn"
                          >
                            {t.moreDetails}
                            <i className="arrow-icon"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          ) : (
            <div className="no-events-message">
              <p>{t.noEvents}</p>
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Link
            to="/events"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              color: "var(--primary-color)",
              textDecoration: "none",
              fontSize: "15px",
              fontWeight: 500,
              padding: "8px 20px",
              borderRadius: "20px",
              border: "2px solid var(--primary-color)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--primary-color)";
              e.target.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "var(--primary-color)";
            }}
          >
            {t.allEvents} →
          </Link>
        </div>
      </div>
    </section>
  );
}

export default EventSlider;
